import React, { useCallback, useEffect, useRef, useState } from 'react';
import Select from 'react-select';
import { useSocketConnectionId } from '@/contexts/SocketContext';
import CreatableSelect from 'react-select/creatable';
import { Modal } from '@/components/utils/Modal';
import type { Dashboard } from '@/types/dashboard';
import { statusService } from '@services/StatusService';
import { dashboardService } from '@/services/dashboardService';

import './EditProjectColumn.css';
import { Toast } from '@/components/utils/Toast';

interface EditDashboardColumnProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  dashboard?: Dashboard;
  projectId?: number;
  typeConfiguration?: string;
  reloadDashboard?: () => void | null;
}

export const EditDashboardColumn = ({
  open,
  setOpen,
  dashboard,
  projectId,
  typeConfiguration = 'Dashboard',
  reloadDashboard,
}: EditDashboardColumnProps) =>
{
  const socketConnectionId = useSocketConnectionId();
  const [typeConfig, _setTypeConfig] = useState(typeConfiguration);
  void _setTypeConfig; // suppress unused warning
  const [activeLayer, setActiveLayer] = useState<{ LayerId: number; LayerName: string }>();
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState<{ BasicFieldValueId: number; BasicFieldValue: string }[]>(
    [],
  );
  const [layers, setLayers] = useState<{ LayerId: number; LayerName: string }[]>([]);
  const [availableColumns, setAvailableColumns] = useState<
    { DashboardGroupId: number; DashboardGroupName: string; oldDashboardGroupId?: number }[]
  >([]);
  const [selections, setSelections] = useState<Record<string, any>>();
  //notification
  const [notificationMessage, setNotificationMessage] = useState<string>('');
  const [showNotificationMessage, setShowNotificationMessage] = useState<boolean>(false);

  const creatableEditProjectRef = useRef(null);
  const currentInputValue = useRef('');

  const getLayers = useCallback(async () =>
  {
    try
    {
      const data = await dashboardService.GetLayersByProjectId(String(dashboard?.ProjectId));
      if (!Array.isArray(data) || data.length === 0) return;

      const mappedLayers = data.map(({ LayerId, Name }) => ({
        LayerId,
        LayerName: Name,
      }));

      const newSelections = mappedLayers.reduce((acc: any, { LayerName, LayerId }) =>
      {
        acc[LayerName] = {
          layerId: LayerId,
          selectedStatus: null,
          selectedColumn: null,
          tableData: [],
          originalTableData: [],
          sortColumn: null,
          sortOrder: null,
        };
        return acc;
      }, {});

      setLayers(mappedLayers);
      setSelections(newSelections);

      // Load status data based on type
      if (typeConfig === 'Project')
      {
        await getProjectStatus(newSelections);
      }
      else if (typeConfig === 'Dashboard')
      {
        await getDashboardGroupStatus(newSelections);
      }
    }
    catch (error)
    {
      console.error('Error loading layers:', error);
    }
  }, [dashboard]);

  // Get status
  const getStatus = useCallback(async () =>
  {
    try
    {
      const data = await statusService.GetStatus();
      if (Array.isArray(data) && data.length > 0)
      {
        setStatus(data);
      }
    }
    catch (error)
    {
      console.error('Error loading status:', error);
    }
  }, [dashboard]);

  // Get dashboard groups
  const getDashboardGroupById = useCallback(async () =>
  {
    try
    {
      const data = await statusService.GetDashboardGroupById(
        dashboard?.DashboardId?.toString() ?? '',
      );
      if (Array.isArray(data) && data.length > 0)
      {
        setAvailableColumns(data);
      }
    }
    catch (error)
    {
      console.error('Error loading dashboard groups:', error);
    }
  }, [dashboard]);

  // Get dashboard group status
  const getDashboardGroupStatus = useCallback(
    async (currentSelections = selections) =>
    {
      try
      {
        const data = await statusService.GetDashboardGroupStatus(
          dashboard?.DashboardId?.toString() ?? '',
        );
        if (!Array.isArray(data) || data.length === 0) return;

        const updatedSelections = { ...currentSelections };
        Object.entries(updatedSelections).forEach(([_key, element]: [string, any]) =>
        {
          const filtered = data.filter((x: any) => x.LayerId === element.layerId);
          element.tableData = filtered;
          element.originalTableData = filtered;
        });

        setSelections(updatedSelections);
      }
      catch (error)
      {
        console.error('Error loading dashboard status:', error);
      }
    },
    [selections, dashboard],
  );

  // Get project status
  const getProjectStatus = useCallback(
    async (currentSelections = selections) =>
    {
      try
      {
        const data = await statusService.GetProjectStatus(String(dashboard?.ProjectId));
        if (!Array.isArray(data) || data.length === 0) return;

        const updatedSelections = { ...currentSelections };
        Object.entries(updatedSelections).forEach(([_key, element]: [string, any]) =>
        {
          const filtered = data.filter((x: any) => x.LayerId === element.layerId);
          element.tableData = filtered;
          element.originalTableData = filtered;
        });

        setSelections(updatedSelections);
      }
      catch (error)
      {
        console.error('Error loading project status:', error);
      }
    },
    [selections, dashboard],
  );

  // Handle sort
  const handleSort = useCallback(
    (column: any) =>
    {
      if (!activeLayer) return;

      setSelections(prevSelections =>
      {
        const currentSelection = prevSelections?.[activeLayer.LayerName];
        const {
          sortColumn: currentSortColumn,
          sortOrder: currentSortOrder,
          originalTableData,
        } = currentSelection;

        const isSameColumn = currentSortColumn === column;
        // Cycle: null → asc → desc → null
        let newSortOrder: string | null;
        if (!isSameColumn || currentSortOrder === null)
        {
          newSortOrder = 'asc';
        }
        else if (currentSortOrder === 'asc')
        {
          newSortOrder = 'desc';
        }
        else
        {
          newSortOrder = null;
        }

        if (newSortOrder === null)
        {
          return {
            ...(prevSelections ?? {}),
            [activeLayer.LayerName]: {
              ...currentSelection,
              tableData: [...(originalTableData ?? [])],
              sortColumn: null,
              sortOrder: null,
            },
          };
        }

        const sortedData = [...currentSelection.tableData].sort((a: any, b: any) =>
        {
          if (a[column] < b[column]) return newSortOrder === 'asc' ? -1 : 1;
          if (a[column] > b[column]) return newSortOrder === 'asc' ? 1 : -1;
          return 0;
        });

        return {
          ...(prevSelections ?? {}),
          [activeLayer.LayerName]: {
            ...currentSelection,
            tableData: sortedData,
            sortColumn: column,
            sortOrder: newSortOrder,
          },
        };
      });
    },
    [activeLayer],
  );

  // Handle layer change
  const handleLayerChange = (layer: any) =>
  {
    setActiveLayer(layer);
  };

  // Handle status change
  const handleStatusChange = useCallback(
    (selectedStatus: any) =>
    {
      if (!activeLayer) return;

      setSelections(prevSelections => ({
        ...(prevSelections ?? {}),
        [activeLayer.LayerName]: {
          ...(prevSelections?.[activeLayer.LayerName] ?? {}),
          selectedStatus,
        },
      }));
    },
    [activeLayer],
  );

  // Handle add
  const handleAdd = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) =>
  {
    e.preventDefault();
    e.stopPropagation();
    if (!activeLayer) return;

    const currentSelection = selections?.[activeLayer.LayerName];
    const { selectedStatus, selectedColumn, tableData } = currentSelection;

    // Handle creatable input
    const rawValue = currentInputValue.current?.trim();
    if (rawValue)
    {
      currentInputValue.current = '';
      onNewOptionClick({
        className: 'Select-create-option-placeholder',
        label: rawValue,
        value: rawValue,
      });
      return;
    }

    if (selectedStatus && selectedColumn)
    {
      const newData = {
        LayerId: activeLayer.LayerId,
        Checked: false,
        BasicFieldValueId: selectedStatus.value,
        Status: selectedStatus.label,
        DashboardGroupId: typeConfig === 'Project' ? '' : selectedColumn.value,
        Column: typeConfig === 'Project' ? selectedColumn : selectedColumn.label,
      };

      setSelections(prevSelections => ({
        ...(prevSelections ?? {}),
        [activeLayer.LayerName]: {
          ...currentSelection,
          selectedStatus: null,
          selectedColumn: null,
          tableData: [...(tableData ?? []), newData],
          originalTableData: [...(currentSelection.originalTableData ?? []), newData],
        },
      }));
    }
  };

  // Handle remove column
  const handleRemoveColumn = useCallback(() =>
  {
    if (!activeLayer) return;

    setSelections(prevSelections =>
    {
      const layerSelection = prevSelections?.[activeLayer.LayerName] ?? {};
      const removed = new Set(
        (layerSelection.tableData ?? [])
          .filter((item: any) => item.Checked)
          .map((item: any) => item.BasicFieldValueId),
      );
      return {
        ...(prevSelections ?? {}),
        [activeLayer.LayerName]: {
          ...layerSelection,
          tableData: (layerSelection.tableData ?? []).filter((item: any) => !item.Checked),
          originalTableData: (layerSelection.originalTableData ?? []).filter(
            (item: any) => !removed.has(item.BasicFieldValueId),
          ),
        },
      };
    });
  }, [activeLayer]);

  // Handle checkbox change
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) =>
  {
    if (!activeLayer) return;

    setSelections(prevSelections =>
    {
      const updatedTableData = [...(prevSelections?.[activeLayer.LayerName]?.tableData ?? [])];
      updatedTableData[index].Checked = e.target.checked;

      return {
        ...(prevSelections ?? {}),
        [activeLayer.LayerName]: {
          ...(prevSelections?.[activeLayer.LayerName] ?? {}),
          tableData: updatedTableData,
        },
      };
    });
  };

  // Show notification
  const showNotification = (message: string) =>
  {
    setNotificationMessage(message);
    setShowNotificationMessage(true);
  };

  // Save project
  const saveProject = useCallback(async () =>
  {
    const tableData: any[] = [];

    Object.entries(selections ?? {}).forEach(([_key, element]) =>
    {
      if (element.tableData.length > 0)
      {
        tableData.push(...element.tableData);
      }
    });

    const request = {
      ProjectId: dashboard?.ProjectId,
      RequestProjectStatus: tableData,
    };

    try
    {
      await statusService.SaveProject(request);
      showNotification('Changes saved successfully');
      setOpen(false);
    }
    catch (error)
    {
      console.error('Error saving project:', error);
      showNotification('Error saving changes');
    }
  }, [selections, showNotification]);

  // Create dashboards
  const createDashboards = useCallback(async () =>
  {
    const tableData: any = [];

    Object.entries(selections ?? {}).forEach(([_key, element]) =>
    {
      if (element.tableData.length > 0)
      {
        tableData.push(...element.tableData);
      }
    });

    // Get unique new columns
    const ids = new Set();
    const uniques = [];
    for (const item of tableData)
    {
      if (!ids.has(item.DashboardGroupId))
      {
        ids.add(item.DashboardGroupId);
        uniques.push(item);
      }
    }

    const newColumns = uniques.filter(x => x.DashboardGroupId < 0);
    const position = availableColumns.filter(x => x.DashboardGroupId > 0).length;
    const promises = [];
    const exitIds: any = [];

    for (let i = 0; i < newColumns.length; i++)
    {
      const item = newColumns[i];

      const columnRequest = {
        Conversations: [],
        DashboardId: dashboard?.DashboardId ?? 0,
        Name: item.Column,
        SocketConnectionId: socketConnectionId,
        TotalItems: 0,
        isNew: true,
        col: (position + i) * 3,
        positionX: (position + i) * 3,
        row: 0,
        sizeX: 3,
        sizeY: 0,
      };

      const promise = dashboardService.AddColumn(columnRequest).then(response =>
      {
        exitIds[i] = response.DashboardGroupId;
      });

      promises.push(promise);
    }

    try
    {
      await Promise.all(promises);

      const updatedColumns = [...availableColumns];
      let count = 0;

      for (let i = 0; i < updatedColumns.length; i++)
      {
        const col = updatedColumns[i];
        if (col && col.DashboardGroupId < 0)
        {
          col.oldDashboardGroupId = col.DashboardGroupId;
          col.DashboardGroupId = exitIds[count];
          count++;
        }
      }

      setAvailableColumns(updatedColumns);
      await saveDashboard(updatedColumns);
    }
    catch (error)
    {
      console.error('Error creating dashboards:', error);
      showNotification('Error creating dashboards');
    }
  }, [selections, availableColumns, showNotification]);

  // Save dashboard
  const saveDashboard = useCallback(
    async (updatedColumns = availableColumns) =>
    {
      const tableData: any = [];

      Object.entries(selections ?? {}).forEach(([_key, element]) =>
      {
        if (element.tableData.length > 0)
        {
          tableData.push(...element.tableData);
        }
      });

      // Update dashboard group IDs for new columns
      if (tableData.some((item: any) => item.DashboardGroupId < 0))
      {
        for (let i = 0; i < tableData.length; i++)
        {
          if (tableData[i].DashboardGroupId < 0)
          {
            const column = updatedColumns.find(
              x => x.oldDashboardGroupId === tableData[i].DashboardGroupId,
            );
            if (column)
            {
              tableData[i].DashboardGroupId = column.DashboardGroupId;
            }
          }
        }
      }

      const request = {
        DashboardId: dashboard?.DashboardId,
        RequestDashboardStatus: tableData,
      };

      try
      {
        await statusService.SaveDashboard(request);
        reloadDashboard && reloadDashboard();
        setOpen(false);
        showNotification('Changes saved successfully');
      }
      catch (error)
      {
        console.error('Error saving dashboard:', error);
        showNotification('Error saving changes');
      }
    },
    [selections, availableColumns, typeConfig, showNotification],
  );

  // Save
  const save = useCallback(async () =>
  {
    if (typeConfig === 'Project')
    {
      await saveProject();
    }
    else if (typeConfig === 'Dashboard')
    {
      if (availableColumns.some(item => item.DashboardGroupId < 0))
      {
        await createDashboards();
      }
      else
      {
        await saveDashboard();
      }
    }
  }, [typeConfig, availableColumns, saveProject, createDashboards, saveDashboard]);

  // Handle column change
  const handleColumnChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) =>
    {
      if (!activeLayer) return;
      const processedColumn = typeConfiguration === 'Project' ? e.target?.value : e;
      setSelections(prevSelections => ({
        ...(prevSelections ?? {}),
        [activeLayer.LayerName]: {
          ...(prevSelections?.[activeLayer.LayerName] ?? {}),
          selectedColumn: processedColumn,
        },
      }));
    },
    [activeLayer, typeConfiguration],
  );

  // Handle column change for CreatableSelect (Dashboard mode)
  const handleCreatableColumnChange = useCallback(
    (selectedOption: any) =>
    {
      if (!activeLayer) return;
      setSelections(prevSelections => ({
        ...(prevSelections ?? {}),
        [activeLayer.LayerName]: {
          ...(prevSelections?.[activeLayer.LayerName] ?? {}),
          selectedColumn: selectedOption,
        },
      }));
    },
    [activeLayer],
  );

  // Handle new option click (create new column)
  const onNewOptionClick = useCallback(
    (inputValue: any) =>
    {
      const id = Math.min(...availableColumns.map(x => x.DashboardGroupId));
      const obj = {
        DashboardGroupId: id > 0 ? -1 : id - 1,
        DashboardGroupName: inputValue,
      };

      const updatedColumns = [...availableColumns, obj];
      setAvailableColumns(updatedColumns);

      // Set the newly created option as selected
      const newOption = {
        label: obj.DashboardGroupName,
        value: obj.DashboardGroupId,
      };

      handleCreatableColumnChange(newOption);
    },
    [availableColumns, handleCreatableColumnChange],
  );

  const resetData = (): void =>
  {
    setActiveLayer(undefined);
    setIsLoading(false);
    setStatus([]);
    setLayers([]);
    setAvailableColumns([]);
    setSelections(undefined);
    setNotificationMessage('');
    setShowNotificationMessage(false);
  };

  // Component mount effect
  useEffect(() =>
  {
    const loadData = async () =>
    {
      if ((dashboard?.DashboardId ?? 0) > 0)
      {
        await getLayers();
        await getStatus();
        await getDashboardGroupById();
      }
      else if ((projectId || 0) > 0)
      {
        await getLayers();
        await getStatus();
      }
    };

    open && loadData();
    !open && resetData();
  }, [open]);

  // Render helpers
  const isDark = document.documentElement.classList.contains('dark');

  const selectDarkStyles = isDark
    ? {
      control: (base: any) => ({
        ...base,
        backgroundColor: '#3a3c3c',
        borderColor: '#4a4c4c',
        borderRadius: 5,
        '&:hover': { borderColor: '#6e6860' },
      }),
      menu: (base: any) => ({ ...base, backgroundColor: '#333535', marginTop: 0 }),
      option: (base: any, state: any) => ({
        ...base,
        backgroundColor: state.isFocused ? '#3c6c80' : state.isSelected ? '#3c6c80' : '#333535',
        color: '#a9a093',
      }),
      singleValue: (base: any) => ({ ...base, color: '#a9a093' }),
      input: (base: any) => ({ ...base, color: '#a9a093' }),
      placeholder: (base: any) => ({ ...base, color: '#6e6860' }),
      dropdownIndicator: (base: any) => ({ ...base, color: '#6e6860' }),
      clearIndicator: (base: any) => ({ ...base, color: '#6e6860' }),
      indicatorSeparator: (base: any) => ({ ...base, backgroundColor: '#4a4c4c' }),
    }
    : {
      control: (base: any) => ({ ...base, borderRadius: 5 }),
      menu: (base: any) => ({ ...base, marginTop: 0 }),
    };

  // Derived values
  const currentSelection = activeLayer
    ? (selections?.[activeLayer.LayerName] ?? {
      selectedStatus: null,
      selectedColumn: null,
      tableData: [],
      originalTableData: [],
      sortOrder: null,
      sortColumn: null,
    })
    : {
      selectedStatus: null,
      selectedColumn: null,
      tableData: [],
      originalTableData: [],
      sortOrder: null,
      sortColumn: null,
    };

  const { selectedStatus, selectedColumn, tableData, sortOrder, sortColumn } = currentSelection;

  const selectedStatuses = tableData.map((item: any) => item.Status);
  const selectedColumns = tableData.map((item: any) => item.Column);

  const statusOptions = status
    .map(s => ({ label: s.BasicFieldValue, value: s.BasicFieldValueId }))
    .filter(option => !selectedStatuses.includes(option.label));

  const columnOptions = availableColumns
    .map(col => ({ label: col.DashboardGroupName, value: col.DashboardGroupId }))
    .filter(option => !selectedColumns.includes(option.label));

  return (
    <>
      <Modal
        open={open}
        onClose={() =>
        {
          setOpen(false);
        }}
        title={typeConfig === 'Project' ? 'Edit Project Column' : 'Edit Dashboard Column'}
        preventCloseClickOut
      >
        <div className="ConfigurationStatus modal-content w-96 max-w-96 my-0 bg-white dark:bg-[#333535] dark:text-[#a9a093] text-xs">
          {/* Body */}
          <div>
            {typeConfig === 'Dashboard' && (
              <p className="my-2 text-wrap dark:text-[#a9a093]">
                To configure new columns go to 'Edit Projects Column option', it is located in the
                action menu from the project banner
              </p>
            )}

            <div className="cstatus-modal-body-divider-first">
              <div className="corgee-tool-module-instructions my-2">
                <strong>Step 1:</strong> Select a layer to configure
              </div>
              <div className="cstatus-modal-input-container">
                <label className="stime-label pull-left pr-[5px]">Layer:</label>
                <div
                  id="select-render-cstatus-Layers"
                  className="corgee_com_custom-fields-combobox-single _DEL_comboBoxRef _DEL_multiselect-hidden-control-container input-group pl-[3px]"
                >
                  <Select
                    autoFocus={false}
                    options={layers}
                    onChange={handleLayerChange}
                    value={activeLayer}
                    isMulti={false}
                    onBlur={() => {}}
                    isSearchable={true}
                    openMenuOnFocus={true}
                    closeMenuOnSelect={true}
                    isClearable
                    getOptionLabel={option => option.LayerName}
                    getOptionValue={option => String(option.LayerId)}
                    theme={theme => ({
                      ...theme,
                      borderRadius: 0,
                      colors: {
                        ...theme.colors,
                        primary25: isDark ? '#3c6c80' : '#e0f2fe',
                        primary: isDark ? '#4a4c4c' : '#dedede',
                      },
                    })}
                    styles={selectDarkStyles}
                    menuPlacement="bottom"
                    menuPosition="absolute"
                  />
                </div>
              </div>
            </div>

            {activeLayer && (
              <div className="cstatus-modal-body-divider">
                <div className="corgee-tool-module-instructions my-2">
                  <strong>Step 2:</strong> Choose the status and column to establish a relation.
                </div>
                <div className="cstatus-modal-input-container">
                  <label className="stime-label pull-left">Status:</label>
                  <div
                    id="select-render-cstatus-Status"
                    className="corgee_com_custom-fields-combobox-single _DEL_comboBoxRef _DEL_multiselect-hidden-control-container input-group flex-1 ml-[30px]"
                  >
                    <Select
                      autoFocus={false}
                      options={statusOptions}
                      onChange={handleStatusChange}
                      value={selectedStatus}
                      isMulti={false}
                      onBlur={() => {}}
                      isClearable
                      isSearchable={true}
                      openMenuOnFocus={true}
                      closeMenuOnSelect={true}
                      theme={theme => ({
                        ...theme,
                        borderRadius: 0,
                        colors: {
                          ...theme.colors,
                          primary25: isDark ? '#3c6c80' : '#e0f2fe',
                          primary: isDark ? '#4a4c4c' : '#dedede',
                        },
                      })}
                      styles={selectDarkStyles}
                      menuPlacement="bottom"
                      menuPosition="absolute"
                    />
                  </div>
                </div>
              </div>
            )}

            {activeLayer && (
              <div className="cstatus-modal-body-divider">
                <div className="cstatus-modal-input-container">
                  <label className="stime-label pull-left">Column:</label>
                  <div
                    id="select-render-cstatus-Column"
                    className="corgee_com_custom-fields-combobox-single _DEL_comboBoxRef _DEL_multiselect-hidden-control-container input-group "
                  >
                    {typeConfig === 'Dashboard' && (
                      <div className="select-column-cstatus-select">
                        <CreatableSelect
                          className="flex-1"
                          ref={creatableEditProjectRef}
                          value={selectedColumn}
                          options={columnOptions}
                          onChange={handleCreatableColumnChange}
                          onCreateOption={onNewOptionClick}
                          autoFocus={false}
                          placeholder="Select or create column"
                          isClearable
                          theme={theme => ({
                            ...theme,
                            borderRadius: 0,
                            colors: {
                              ...theme.colors,
                              primary25: isDark ? '#3c6c80' : '#e0f2fe',
                              primary: isDark ? '#4a4c4c' : '#dedede',
                            },
                          })}
                          styles={selectDarkStyles}
                          isSearchable={true}
                          openMenuOnFocus={true}
                          closeMenuOnSelect={true}
                          onBlur={() => {}}
                          menuPlacement="bottom"
                          menuPosition="absolute"
                        />
                        <button onClick={handleAdd} className="btn button-add-column-cstatus">
                          Add
                        </button>
                      </div>
                    )}

                    {typeConfig === 'Project' && (
                      <div className="select-column-cstatus flex">
                        <input
                          className="project-column-name-cstatus flex-1 border-r-[5px] w-[268px]"
                          value={selectedColumn || ''}
                          onChange={e => handleColumnChange(e)}
                        />
                        <button onClick={handleAdd} className="btn button-add-column-cstatus mr-5">
                          Add
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeLayer && (
              <table className="status-table-cstatus">
                <thead>
                  <tr>
                    <th>
                      {tableData.some((x: any) => x.Checked) ? (
                        <div className="remove-link-cstatus " onClick={handleRemoveColumn}>
                          Remove
                        </div>
                      ) : (
                        'Select'
                      )}
                    </th>
                    <th onClick={() => handleSort('Status')} className="cursor-pointer">
                      Status {sortColumn === 'Status' ? (sortOrder === 'asc' ? ' ▲' : ' ▼') : ''}
                    </th>
                    <th onClick={() => handleSort('Column')} className="cursor-pointer">
                      Column {sortColumn === 'Column' ? (sortOrder === 'asc' ? ' ▲' : ' ▼') : ''}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {tableData.map((item: any, index: any) => (
                    <tr key={index}>
                      <td>
                        <input
                          type="checkbox"
                          checked={item.Checked || false}
                          onChange={e => handleCheckboxChange(e, index)}
                        />
                      </td>
                      <td>{item.Status}</td>
                      <td>{item.Column}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {isLoading && (
              <div className="text-center">
                <div className="ifj-spinner-border" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="pt-3 text-right gap-[10px] relative overflow-hidden min-h-6 flex flex-row-reverse">
            <button
              type="button"
              onClick={save}
              className="bg-corgee-blue dark:bg-[#3c6c80] dark:hover:brightness-[0.85]hover:bg-corgee-BlueHover dark:hover:brightness-[0.85] text-white text-xs h-7 px-3 py-1"
            >
              Save
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="bg-brightGray hover:bg-corgee-brightGrayHover text-white text-xs h-7 px-3 py-1 dark:bg-[#5c6060] dark:hover:brightness-[0.85]"
            >
              Cancel
            </button>
          </div>
        </div>
      </Modal>
      <Toast
        open={showNotificationMessage}
        message={notificationMessage}
        onClose={() => setShowNotificationMessage(false)}
      />
    </>
  );
};
